import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Toaster,
  useToastController,
  useId,
  Toast,
  ToastTitle,
  mergeClasses,
} from '@fluentui/react-components';
import { usePlannerScreenStyles } from './PlannerScreen.styles';
import { telemetry } from '@/services/telemetry';
import { TELEMETRY_COMPONENT } from '@/constants';
import { EventDataGrid, type EventDataGridSortState } from '@/components/planner/EventDataGrid';
import { EventListToolbar } from '@/components/planner/EventListToolbar';
import { EventFilterPanel } from '@/components/planner/EventFilterPanel';
import { EventFilterTagBar } from '@/components/planner/EventFilterTagBar';
import { EventActionBar } from '@/components/planner/EventActionBar/EventActionBar';
import { ClaimEventsDialog } from '@/components/planner/ClaimEventsDialog/ClaimEventsDialog';
import { HandoffDialog } from '@/components/planner/HandoffDialog/HandoffDialog';
import { EventReviewPanel } from '@/components/planner/EventReviewPanel';
import { DeclineDialog } from '@/components/planner/DeclineDialog';
import { CancelBookingDialog } from '@/components/planner/CancelBookingDialog';
import { CancelSuccessDialog } from '@/components/planner/CancelSuccessDialog';
import { UpdateSuccessDialog } from '@/components/planner/UpdateSuccessDialog';
import { EventRequestDetails } from '@/components/dashboard/EventRequestDetails';
import { CalendarPanel } from '@/layouts';
import { useEventFilters } from '@/hooks/useEventFilters';
import { useEventActions } from '@/hooks/useEventActions/useEventActions';
import { useHandoffPlanners } from '@/hooks/useHandoffPlanners';
import { useOwnerFilterOptions } from '@/hooks/useOwnerFilterOptions';
import { usePlannerEvents } from '@/hooks/usePlannerEvents';
import { useDebounce } from '@/hooks/useDebounce';
import { useOwnerPhotos } from '@/hooks/useOwnerPhotos';
import { useReviewActions } from '@/hooks/useReviewActions/useReviewActions';
import { useExportEvents } from '@/hooks/useExportEvents';
import { haveSelectedRoomsChanged } from './roomSelectionChange';
import type { PlannerEventStatus, PlannerEventRow, OwnerOption } from '@/types/planner.types';
import type { EventId } from '@/store/models/ids';
import { useGetPlannerBuildingsQuery } from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  setSearchQuery,
  setSelectedEventIds,
  setFilterPanelOpen,
  setSortState,
  resetPlannerUi,
  selectPlannerSearchQuery,
  selectPlannerSelectedEventIds,
  selectPlannerFilterPanelOpen,
  selectPlannerSortState,
} from '@store/slices';

const FILTER_STATUS_OPTIONS: PlannerEventStatus[] = [
  'New request',
  'Under review',
  'Confirmed',
  'Declined',
  'Request for cancellation',
  'Cancelled',
  'Completed',
  'Did not occur',
];

export const PlannerScreen = () => {
  const styles = usePlannerScreenStyles();
  const { t } = useTranslation('planner');
  const location = useLocation();
  const urlSection = location.pathname.replace(/^\/planner\/?/, '').split('/')[0];
  // Default to 'event-list' (dashboard removed as out-of-scope; spec 101).
  const selectedNav = urlSection && urlSection.length > 0 ? urlSection : 'event-list';

  // Shared planner interaction state lives in the store (FR-016). Selections are kept as a
  // serializable string[] in the slice and exposed as a Set to the existing grid/action APIs.
  const dispatch = useAppDispatch();
  const searchQuery = useAppSelector(selectPlannerSearchQuery);
  const selectedEventIdsArray = useAppSelector(selectPlannerSelectedEventIds);
  const filterPanelOpen = useAppSelector(selectPlannerFilterPanelOpen);
  const sliceSortState = useAppSelector(selectPlannerSortState);
  const selectedEventIds = useMemo(() => new Set(selectedEventIdsArray), [selectedEventIdsArray]);
  const {
    events: apiEvents,
    isLoadingInitial,
    isLoadingMore,
    isError,
    batchError,
    endReached,
    totalCount,
    loadMore,
    retryLastBatch,
    setSortBy,
    setFilters: setApiFilters,
    setSearch,
    refetch,
    applyRowMutation,
  } = usePlannerEvents({ skip: selectedNav !== 'event-list' });
  // Buffer events through local state so the DataGrid stays mounted during refetch.
  // usePlannerEvents.reset() clears accumulated to [], which would otherwise unmount
  // the DataGrid (loading && items.length === 0) and lose column resize state.
  const [events, setEvents] = useState<PlannerEventRow[]>([]);
  useEffect(() => {
    if (apiEvents.length > 0) {
      setEvents((prev) =>
        prev === (apiEvents as PlannerEventRow[]) ? prev : (apiEvents as PlannerEventRow[])
      );
    } else if (!isLoadingInitial) {
      setEvents((prev) => (prev.length === 0 ? prev : []));
    }
  }, [apiEvents, isLoadingInitial]);
  const toasterId = useId('planner-toaster');
  const eventListTitleId = useId('planner-event-list-title');
  const { dispatchToast } = useToastController(toasterId);

  const reviewActions = useReviewActions();
  const [confirmedEventId, setConfirmedEventId] = useState<string | null>(null);
  const [cancelSuccessOpen, setCancelSuccessOpen] = useState(false);
  const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false);
  const [updatedRoomChanged, setUpdatedRoomChanged] = useState(false);
  const [openPanelInEditMode, setOpenPanelInEditMode] = useState(false);

  const handleCloseConfirmedDetails = useCallback(() => {
    setConfirmedEventId(null);
    requestAnimationFrame(() => {
      panelTriggerRef.current?.focus();
      panelTriggerRef.current = null;
    });
  }, []);

  const handleEditFromDetails = useCallback(() => {
    const eventId = confirmedEventId;
    setConfirmedEventId(null);
    if (eventId) {
      reviewActions.openPanel(eventId);
      setOpenPanelInEditMode(true);
    }
  }, [confirmedEventId, reviewActions]);

  const availableOwners = useOwnerFilterOptions();

  const { data: plannerBuildingsData } = useGetPlannerBuildingsQuery(undefined, {
    skip: selectedNav !== 'event-list',
  });
  const availableBuildings = useMemo<OwnerOption[]>(
    () =>
      [...(plannerBuildingsData?.buildings ?? [])]
        .map((b) => ({ id: b.buildingId, displayName: b.buildingName }))
        .sort((a, b) =>
          a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
        ),
    [plannerBuildingsData]
  );

  const {
    filters,
    applyFilters,
    hasActiveFilters,
    filterPills,
    removeFilter,
    updateFilter,
    clearAllFilters,
  } = useEventFilters({
    availableOwners: availableOwners.options,
    availableBuildings,
    buildingLabel: `${t('filter.building')}:`,
  });

  const handleSelectionClear = useCallback(() => {
    dispatch(setSelectedEventIds([]));
  }, [dispatch]);

  const handleEventsUpdate = useCallback(
    (updatedEvents: PlannerEventRow[]) => {
      // Propagate row mutations into the hook's accumulating window so they
      // persist across re-renders and survive the apiEvents → events sync (FR-015).
      const prevById = new Map(events.map((e) => [e.eventId, e]));
      for (const updated of updatedEvents) {
        const prev = prevById.get(updated.eventId);
        if (!prev) continue;
        // Shallow-diff to find changed top-level fields. Owner is compared by name.
        const patch: Partial<PlannerEventRow> = {};
        let changed = false;
        if (
          prev.owner.name !== updated.owner.name ||
          prev.owner.avatarUrl !== updated.owner.avatarUrl
        ) {
          patch.owner = updated.owner;
          changed = true;
        }
        if (prev.status !== updated.status) {
          patch.status = updated.status;
          changed = true;
        }
        if (changed) {
          applyRowMutation(updated.eventId, patch);
        }
      }
    },
    [events, applyRowMutation]
  );

  const eventActions = useEventActions({
    selectedEventIds,
    events,
    onSelectionClear: handleSelectionClear,
    onEventsUpdate: handleEventsUpdate,
  });

  const showSuccessToast = useCallback(
    (message: string) => {
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent: 'success', timeout: 3000 }
      );
    },
    [dispatchToast]
  );

  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [handoffDialogOpen, setHandoffDialogOpen] = useState(false);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const dialogTriggerRef = useRef<HTMLElement | null>(null);
  const panelTriggerRef = useRef<HTMLElement | null>(null);

  const handleOpenClaim = useCallback(() => {
    dialogTriggerRef.current = document.activeElement as HTMLElement | null;
    eventActions.openClaimDialog();
    setClaimDialogOpen(true);
  }, [eventActions]);

  const handoffPlanners = useHandoffPlanners();

  const handleOpenHandoff = useCallback(() => {
    dialogTriggerRef.current = document.activeElement as HTMLElement | null;
    eventActions.openHandoffDialog();
    setHandoffDialogOpen(true);
    handoffPlanners.fetchPlanners(selectedEventIds, events);
  }, [eventActions, handoffPlanners, selectedEventIds, events]);

  const handleCancelDialog = useCallback(() => {
    eventActions.closeDialog();
    setClaimDialogOpen(false);
    setHandoffDialogOpen(false);
    handoffPlanners.reset();
    // Restore focus to the button that opened the dialog
    requestAnimationFrame(() => {
      dialogTriggerRef.current?.focus();
      dialogTriggerRef.current = null;
    });
  }, [eventActions, handoffPlanners]);

  const handleConfirmEvent = useCallback(async () => {
    const eventId = reviewActions.selectedEventId;
    const didConfirm = await reviewActions.confirmEvent();
    if (didConfirm) {
      refetch();
      showSuccessToast(t('dialogs.toast.confirmSuccess'));
    }
    if (didConfirm && eventId) {
      setConfirmedEventId(eventId);
    }
  }, [reviewActions, refetch, showSuccessToast, t]);

  const handleDeclineEvent = useCallback(
    async (reason: string) => {
      const didDecline = await reviewActions.declineEvent(reason);
      if (didDecline) {
        refetch();
        showSuccessToast(t('dialogs.toast.declineSuccess'));
      }
    },
    [reviewActions, refetch, showSuccessToast, t]
  );

  const handleCancelEvent = useCallback(
    async (reason: string) => {
      const didCancel = await reviewActions.cancelEvent(reason);
      if (didCancel) {
        refetch();
        setCancelSuccessOpen(true);
      }
    },
    [reviewActions, refetch]
  );

  const handleConfirmCancellation = useCallback(async () => {
    const didCancel = await reviewActions.confirmCancellation();
    if (didCancel) {
      refetch();
      setCancelSuccessOpen(true);
    }
  }, [reviewActions, refetch]);

  const handleNoOccurrence = useCallback(async () => {
    const didMark = await reviewActions.markNoOccurrence();
    if (didMark) {
      refetch();
      showSuccessToast(t('dialogs.toast.noOccurrenceSuccess'));
    }
  }, [reviewActions, refetch, showSuccessToast, t]);

  const handleSaveEdit = useCallback(
    async (input: import('@/types/planner.types').EventReviewEditInput) => {
      const isConfirmedEvent = reviewActions.event?.eventState === 'CONFIRMED';
      const roomsChanged = haveSelectedRoomsChanged(
        reviewActions.event,
        reviewActions.selectedSpaces
      );
      const didSave = await reviewActions.saveEdit(input);
      if (didSave) {
        refetch();
        if (isConfirmedEvent) {
          setUpdatedRoomChanged(roomsChanged);
          setUpdateSuccessOpen(true);
        } else {
          showSuccessToast(t('dialogs.toast.editSuccess'));
        }
      }
      return didSave;
    },
    [reviewActions, refetch, showSuccessToast, t]
  );

  const handleSpaceChange = useCallback(
    async (spaceId: string) => {
      return reviewActions.setSelectedSpace(spaceId);
    },
    [reviewActions]
  );

  const handleConfirmAction = useCallback(() => {
    eventActions.confirmAction();
  }, [eventActions]);

  // Close dialog + clear selection when processing finishes (isProcessing goes from true → false)
  const wasProcessingRef = useRef(false);
  useEffect(() => {
    if (wasProcessingRef.current && !eventActions.isProcessing) {
      setClaimDialogOpen(false);
      setHandoffDialogOpen(false);

      // Only refetch and clear selection when at least one event succeeded.
      // On complete failure (e.g. handoff on unclaimed events) preserve the
      // current list view and filters so the user can retry or adjust.
      const hadSuccess = (eventActions.lastActionResult?.successCount ?? 0) > 0;
      if (hadSuccess) {
        dispatch(setSelectedEventIds([]));
        refetch();
        if (reviewActions.panelOpen) {
          reviewActions.refetch();
        }
        // Return focus to the grid after dialog closes — prevents focus from
        // escaping to the footer when the action bar hides.
        requestAnimationFrame(() => {
          dialogTriggerRef.current = null;
          gridContainerRef.current?.focus();
        });
      } else {
        // On failure the action bar stays visible — restore focus to the trigger button
        requestAnimationFrame(() => {
          dialogTriggerRef.current?.focus();
          dialogTriggerRef.current = null;
        });
      }
    }
    wasProcessingRef.current = eventActions.isProcessing;
  }, [eventActions.isProcessing, eventActions.lastActionResult, refetch, reviewActions, dispatch]);

  // Show success toast when action completes with successful events
  const shownResultRef = useRef<typeof eventActions.lastActionResult>(null);
  useEffect(() => {
    const result = eventActions.lastActionResult;
    if (!result) {
      shownResultRef.current = null;
      return;
    }
    if (result.successCount === 0 || result === shownResultRef.current) return;
    shownResultRef.current = result;
    if (result.type === 'claim') {
      showSuccessToast(t('dialogs.toast.claimSuccess', { count: result.successCount }));
    } else if (result.type === 'handoff') {
      showSuccessToast(
        t('dialogs.toast.handoffSuccess', {
          count: result.successCount,
          plannerName: result.plannerName,
        })
      );
    }
  }, [eventActions.lastActionResult, showSuccessToast, t]);

  // Show error toast when action has failures
  const shownErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!eventActions.lastActionError) {
      shownErrorRef.current = null;
      return;
    }
    if (eventActions.lastActionError === shownErrorRef.current) return;
    shownErrorRef.current = eventActions.lastActionError;
    dispatchToast(
      <Toast>
        <ToastTitle>{eventActions.lastActionError}</ToastTitle>
      </Toast>,
      { intent: 'error', timeout: 5000 }
    );
  }, [eventActions.lastActionError, dispatchToast]);

  const resolveReviewErrorMessage = useCallback(
    (errorCodeOrMessage: string): string => {
      if (errorCodeOrMessage === 'conflict' || errorCodeOrMessage === 'invalidState') {
        return t(`reviewPanel.error.${errorCodeOrMessage}`);
      }

      return errorCodeOrMessage;
    },
    [t]
  );

  const shownReviewErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!reviewActions.error) {
      shownReviewErrorRef.current = null;
      return;
    }

    if (reviewActions.error === shownReviewErrorRef.current) {
      return;
    }

    shownReviewErrorRef.current = reviewActions.error;
    dispatchToast(
      <Toast>
        <ToastTitle>{resolveReviewErrorMessage(reviewActions.error)}</ToastTitle>
      </Toast>,
      { intent: 'error', timeout: 5000 }
    );
  }, [reviewActions.error, dispatchToast, resolveReviewErrorMessage]);

  const shownReviewLoadErrorRef = useRef(false);
  useEffect(() => {
    const shouldShowLoadErrorToast = reviewActions.panelOpen && reviewActions.isError;

    if (!shouldShowLoadErrorToast) {
      shownReviewLoadErrorRef.current = false;
      return;
    }

    if (shownReviewLoadErrorRef.current) {
      return;
    }

    shownReviewLoadErrorRef.current = true;
    dispatchToast(
      <Toast>
        <ToastTitle>{t('reviewPanel.error.description')}</ToastTitle>
      </Toast>,
      { intent: 'error', timeout: 5000 }
    );
  }, [reviewActions.panelOpen, reviewActions.isError, dispatchToast, t]);

  useEffect(() => {
    telemetry.trackPageView(TELEMETRY_COMPONENT, 'PlannerScreen');
  }, []);

  // Reset shared planner interaction state on entry so a prior visit's search/selection/sort
  // does not leak into a fresh view (FR-016).
  useEffect(() => {
    dispatch(resetPlannerUi());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filter changes to API (skip on initial mount with empty filters)
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    if (prevFiltersRef.current === filters) return;
    prevFiltersRef.current = filters;
    setApiFilters(filters);
  }, [filters, setApiFilters]);

  // Drive an exhaustive, server-side search from the toolbar query. Debounced so we
  // issue one request after the user stops typing rather than per keystroke. Unlike the
  // previous client-only filter (which matched just the already-loaded page on event
  // name / requester), this searches the entire event set across all searchable fields.
  const debouncedSearch = useDebounce(searchQuery, 400);
  const prevSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearchRef.current === debouncedSearch) return;
    prevSearchRef.current = debouncedSearch;
    setSearch(debouncedSearch);
  }, [debouncedSearch, setSearch]);

  const filteredEvents = useMemo(() => {
    // Deduplicate by eventId — API may return the same event more than once.
    // Search itself is performed server-side (see the debounced effect above), so the
    // accumulated events already represent the exhaustive match set; no client filtering.
    const seen = new Set<string>();
    return events.filter((e) => {
      if (seen.has(e.eventId)) return false;
      seen.add(e.eventId);
      return true;
    });
  }, [events]);

  // Statuses where Claim / Handoff are not allowed.
  const NON_ACTIONABLE_STATUSES: ReadonlySet<string> = useMemo(
    () => new Set(['Cancelled', 'Did not occur']),
    []
  );

  const selectedReviewDisplayTitle = useMemo(() => {
    const selectedId =
      reviewActions.selectedEventId ?? (reviewActions.event?.eventId as string | undefined);
    if (!selectedId) return undefined;
    return filteredEvents.find((event) => event.eventId === selectedId)?.eventName;
  }, [filteredEvents, reviewActions.event?.eventId, reviewActions.selectedEventId]);

  // Planner rows already carry the series occurrence suffix (e.g. " (1/2)") in
  // eventName; reuse it for the confirmed-event details modal title.
  const confirmedEventDisplayTitle = useMemo(
    () => filteredEvents.find((event) => event.eventId === confirmedEventId)?.eventName,
    [filteredEvents, confirmedEventId]
  );

  const hasNonActionableSelection = useMemo(() => {
    if (selectedEventIds.size === 0) return false;
    return filteredEvents.some(
      (e) => selectedEventIds.has(e.eventId) && NON_ACTIONABLE_STATUSES.has(e.status)
    );
  }, [selectedEventIds, filteredEvents, NON_ACTIONABLE_STATUSES]);

  const allSelectedDeclined = useMemo(() => {
    if (selectedEventIds.size === 0) return false;
    const selectedEvents = filteredEvents.filter((e) => selectedEventIds.has(e.eventId));
    return selectedEvents.length > 0 && selectedEvents.every((e) => e.status === 'Declined');
  }, [selectedEventIds, filteredEvents]);

  const sortState: EventDataGridSortState = useMemo(
    () =>
      sliceSortState ?? {
        sortColumn: 'submittedDate',
        sortDirection: 'descending',
      },
    [sliceSortState]
  );

  const handleSortChange = useCallback(
    (state: EventDataGridSortState) => {
      dispatch(
        setSortState({
          sortColumn: typeof state.sortColumn === 'string' ? state.sortColumn : '',
          sortDirection: state.sortDirection,
        })
      );
    },
    [dispatch]
  );

  const ownerPhotoMap = useOwnerPhotos(filteredEvents);

  const handleRowClick = useCallback(
    (eventId: string) => {
      panelTriggerRef.current = document.activeElement as HTMLElement | null;
      reviewActions.openPanel(eventId);
    },
    [reviewActions]
  );

  const handleFilterClick = useCallback(() => {
    dispatch(setFilterPanelOpen(!filterPanelOpen));
  }, [dispatch, filterPanelOpen]);

  const handleFilterApply = useCallback(
    (newFilters: typeof filters) => {
      applyFilters(newFilters);
      dispatch(setFilterPanelOpen(false));
    },
    [applyFilters, dispatch]
  );

  const handleFilterCancel = useCallback(() => {
    dispatch(setFilterPanelOpen(false));
  }, [dispatch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      dispatch(setSearchQuery(value));
    },
    [dispatch]
  );

  // Export is a placeholder action for now (no export pipeline wired up yet).
  const { exportEvents, isExporting } = useExportEvents({
    filters,
    expectLong: (totalCount ?? 0) > 5000,
  });
  const handleExportClick = useCallback(() => {
    void exportEvents();
  }, [exportEvents]);

  const handleSelectionChange = useCallback(
    (ids: Set<string>) => {
      dispatch(setSelectedEventIds(Array.from(ids)));
    },
    [dispatch]
  );

  const handleUpdateFilter = useCallback(
    (category: Parameters<typeof updateFilter>[0], values: string[]) => {
      updateFilter(category, values);
    },
    [updateFilter]
  );

  const handleOpenPanelForDate = useCallback(() => {
    dispatch(setFilterPanelOpen(true));
  }, [dispatch]);

  const renderContent = () => {
    switch (selectedNav) {
      case 'calendar-view':
        return <CalendarPanel />;
      case 'event-list':
        return (
          <section className={styles.eventListContent} aria-labelledby={eventListTitleId}>
            <h1 id={eventListTitleId} className={styles.title}>
              {t('eventList.title')}
            </h1>
            <p className={styles.subtitle}>{t('eventList.subtitle')}</p>
            <EventListToolbar
              loadedCount={apiEvents.length}
              searchMatchCount={filteredEvents.length}
              hasSearchQuery={searchQuery.trim().length > 0}
              totalCount={totalCount ?? apiEvents.length}
              hasActiveFilters={hasActiveFilters}
              onFilterClick={handleFilterClick}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onExport={handleExportClick}
              isExporting={isExporting}
            />
            {hasActiveFilters && (
              <EventFilterTagBar
                pills={filterPills}
                onRemove={removeFilter}
                onClearAll={clearAllFilters}
                onUpdateFilter={handleUpdateFilter}
                onOpenPanelForDate={handleOpenPanelForDate}
                availableOwners={availableOwners.options}
                availableStatuses={FILTER_STATUS_OPTIONS}
                availableBuildings={availableBuildings}
                currentOwners={filters.owners}
                currentStatuses={filters.statuses}
                currentBuildings={filters.buildings}
                ownerIsLoading={availableOwners.isLoading}
                ownerErrorState={availableOwners.error}
                onOwnerRetry={availableOwners.retry}
              />
            )}
            <div className={styles.gridActionGroup}>
              <div ref={gridContainerRef} tabIndex={-1} style={{ outline: 'none' }}>
                <EventDataGrid
                  items={filteredEvents}
                  ownerPhotoMap={ownerPhotoMap}
                  selectedItems={selectedEventIds}
                  onRowClick={handleRowClick}
                  onSelectionChange={handleSelectionChange}
                  loading={isLoadingInitial}
                  isLoadingMore={isLoadingMore}
                  isError={isError}
                  batchError={batchError}
                  endReached={endReached}
                  onReachEnd={loadMore}
                  onRetry={refetch}
                  onRetryBatch={retryLastBatch}
                  onSort={setSortBy}
                  // Drive the grid's sort in controlled mode so the column-header sort
                  // indicator and ordering reflect the user's current choice. Without
                  // this, the sort would remain stuck on the initial
                  // 'submittedDate descending' even after the user clicks a column header.
                  sortState={sortState}
                  onSortChange={handleSortChange}
                />
              </div>
              <div
                className={mergeClasses(
                  styles.actionBarWrapper,
                  selectedEventIds.size > 0 ? styles.actionBarVisible : styles.actionBarHidden
                )}
                aria-hidden={selectedEventIds.size === 0}
              >
                <EventActionBar
                  selectedCount={selectedEventIds.size || 0}
                  actionsDisabled={hasNonActionableSelection || allSelectedDeclined}
                  actionsDisabledReason={
                    hasNonActionableSelection
                      ? t('actionBar.disabledReason')
                      : allSelectedDeclined
                        ? t('actionBar.declinedDisabledReason')
                        : undefined
                  }
                  onClaimClick={handleOpenClaim}
                  onHandoffClick={handleOpenHandoff}
                />
              </div>
            </div>
            <ClaimEventsDialog
              open={claimDialogOpen}
              eventCount={eventActions.snapshotCount}
              isProcessing={eventActions.isProcessing}
              error={eventActions.error}
              onConfirm={handleConfirmAction}
              onCancel={handleCancelDialog}
            />
            <HandoffDialog
              open={handoffDialogOpen}
              eventCount={eventActions.snapshotCount}
              planners={handoffPlanners.planners}
              selectedPlanner={eventActions.selectedPlanner}
              onPlannerChange={eventActions.setSelectedPlanner}
              isProcessing={eventActions.isProcessing}
              isLoadingPlanners={handoffPlanners.isLoading}
              error={eventActions.error}
              onConfirm={handleConfirmAction}
              onCancel={handleCancelDialog}
            />
            <EventFilterPanel
              open={filterPanelOpen}
              appliedFilters={filters}
              availableOwners={availableOwners.options}
              availableStatuses={FILTER_STATUS_OPTIONS}
              availableBuildings={availableBuildings}
              onApply={handleFilterApply}
              onCancel={handleFilterCancel}
              ownerIsLoading={availableOwners.isLoading}
              ownerErrorState={availableOwners.error}
              onOwnerRetry={availableOwners.retry}
            />
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>{renderContent()}</div>
      <Toaster toasterId={toasterId} position="top-end" />
      <EventReviewPanel
        open={reviewActions.panelOpen}
        event={reviewActions.event}
        displayTitle={selectedReviewDisplayTitle}
        building={reviewActions.building}
        spaces={
          reviewActions.spaces as unknown as import('@/store/models/spaces').SpaceWithAvailability[]
        }
        buildings={reviewActions.buildings}
        selectedSpaceStateProvince={reviewActions.selectedSpaceStateProvince}
        isLoading={reviewActions.isLoading}
        isError={reviewActions.isError}
        selectedSpaceId={reviewActions.selectedSpaceId}
        selectedSpaces={reviewActions.selectedSpaces}
        onSelectedSpacesChange={reviewActions.setSelectedSpaces}
        isConfirming={reviewActions.isConfirming}
        isDeclining={reviewActions.isDeclining}
        isMarkingNoOccurrence={reviewActions.isMarkingNoOccurrence}
        isSavingEdit={reviewActions.isSavingEdit}
        error={reviewActions.error}
        onClose={() => {
          reviewActions.closePanel();
          setOpenPanelInEditMode(false);
          requestAnimationFrame(() => {
            panelTriggerRef.current?.focus();
            panelTriggerRef.current = null;
          });
        }}
        onConfirm={() => void handleConfirmEvent()}
        onDeclineClick={reviewActions.openDeclineDialog}
        onCancelClick={() => {
          if (reviewActions.event?.eventState === 'SUBMITTED_FOR_CANCELLATION') {
            void handleConfirmCancellation();
          } else {
            reviewActions.openCancelDialog();
          }
        }}
        onNoOccurrenceClick={() => void handleNoOccurrence()}
        onSaveEdit={handleSaveEdit}
        onSpaceChange={handleSpaceChange}
        onClearSpaceChange={reviewActions.clearSelectedSpace}
        onFetchSpaces={reviewActions.fetchSpacesAvailability}
        isSearchingSpaces={reviewActions.isSearchingSpaces}
        onResendSurvey={reviewActions.resendSurvey}
        isResendingSurvey={reviewActions.isResendingSurvey}
        onRetry={reviewActions.refetch}
        initialEditing={openPanelInEditMode}
        toasterId={toasterId}
      />
      <DeclineDialog
        open={reviewActions.declineDialogOpen}
        isProcessing={reviewActions.isDeclining}
        error={reviewActions.error}
        onConfirm={(reason: string) => void handleDeclineEvent(reason)}
        onCancel={reviewActions.closeDeclineDialog}
      />
      <CancelBookingDialog
        open={reviewActions.cancelDialogOpen}
        eventTitle={selectedReviewDisplayTitle ?? reviewActions.event?.eventTitle ?? ''}
        isProcessing={reviewActions.isCancelling}
        error={reviewActions.error}
        onConfirm={(reason: string) => void handleCancelEvent(reason)}
        onCancel={reviewActions.closeCancelDialog}
      />
      <CancelSuccessDialog open={cancelSuccessOpen} onClose={() => setCancelSuccessOpen(false)} />
      <UpdateSuccessDialog
        open={updateSuccessOpen}
        roomChanged={updatedRoomChanged}
        onClose={() => setUpdateSuccessOpen(false)}
      />
      <EventRequestDetails
        open={confirmedEventId !== null}
        eventId={confirmedEventId as EventId | null}
        onClose={handleCloseConfirmedDetails}
        onEditClick={handleEditFromDetails}
        titleOverride={confirmedEventDisplayTitle}
      />
    </div>
  );
};

PlannerScreen.displayName = 'PlannerScreen';
